import React from 'react';
import {Alert, Button, Stack, Typography} from '@mui/material';
import {useNavigate} from 'react-router';
import {useSession} from '../../firebase/UserProvider';
import {cloneWhiteboard, createWhiteboard} from '../../firebase/firestore/whiteboards';
import {useWhiteboards} from './hooks/useWhiteboards';
import {TABLE_COLUMNS} from './constants';
import WhiteboardsTable from './components/WhiteboardsTable';
import {preloadWhiteboardEditor} from './preloadWhiteboardEditor';
import {stopHoverWhiteboardWarmup, warmupWhiteboardRoom, warmupWhiteboardRoomOnHover} from './warmupWhiteboardRoom';

export default function WhiteboardsListPage() {
    const navigate = useNavigate();
    const {user} = useSession();
    const {whiteboards: fetchedWhiteboards, loading, error} = useWhiteboards();
    const [localWhiteboards, setLocalWhiteboards] = React.useState(null);
    const [creating, setCreating] = React.useState(false);
    const [cloningId, setCloningId] = React.useState(null);

    // Sync server-fetched list into local state so we can apply optimistic removals.
    React.useEffect(() => {
        if (fetchedWhiteboards) setLocalWhiteboards(fetchedWhiteboards);
    }, [fetchedWhiteboards]);

    const whiteboards = localWhiteboards ?? fetchedWhiteboards;

    React.useEffect(() => {
        preloadWhiteboardEditor();
        return () => stopHoverWhiteboardWarmup();
    }, []);

    const handleCreate = async () => {
        if (!user || creating) return;
        setCreating(true);
        // Warm the editor chunk in parallel, but never block navigation on it —
        // WhiteboardPage Suspense already shows a loading state for the lazy import.
        void preloadWhiteboardEditor();
        try {
            const {whiteboardId} = await createWhiteboard({user, title: 'Untitled'});
            if (whiteboardId && user?.uid) {
                warmupWhiteboardRoom(whiteboardId, user.uid);
            }
            navigate(`/charts/${whiteboardId}`);
        } catch (err) {
            console.error('Failed to create whiteboard', err);
        } finally {
            setCreating(false);
        }
    };

    const handleClone = async (whiteboardId) => {
        if (!user || cloningId) return;
        setCloningId(whiteboardId);
        void preloadWhiteboardEditor();
        try {
            const {whiteboardId: newId} = await cloneWhiteboard({whiteboardId, user});
            if (newId && user?.uid) {
                warmupWhiteboardRoom(newId, user.uid);
            }
            navigate(`/charts/${newId}`);
        } catch (err) {
            console.error('Failed to clone whiteboard', err);
        } finally {
            setCloningId(null);
        }
    };

    const handleDeleted = (deletedId) => {
        setLocalWhiteboards((prev) => (prev ?? []).filter((w) => w.id !== deletedId));
    };

    const handleTitleClick = (whiteboardId) => {
        void preloadWhiteboardEditor();
        if (user?.uid) {
            warmupWhiteboardRoom(whiteboardId, user.uid);
        }
        navigate(`/charts/${whiteboardId}`);
    };

    const handleTitleHover = (whiteboardId) => {
        void preloadWhiteboardEditor();
        if (user?.uid) {
            warmupWhiteboardRoomOnHover(whiteboardId, user.uid);
        }
    };

    return (
        <Stack p={2} spacing={3}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h5">{whiteboards.length} Charts</Typography>
                <Button
                    variant="contained"
                    onClick={handleCreate}
                    onMouseEnter={preloadWhiteboardEditor}
                    disabled={!user || creating}
                >
                    {creating ? 'Preparing editor…' : 'Create New Chart'}
                </Button>
            </Stack>

            {error && <Alert severity="error">Failed to load charts.</Alert>}

            <WhiteboardsTable
                whiteboards={whiteboards}
                columns={TABLE_COLUMNS}
                loading={loading}
                cloningId={cloningId}
                onTitleClick={handleTitleClick}
                onTitleHover={handleTitleHover}
                onClone={handleClone}
                onDeleted={handleDeleted}
            />
        </Stack>
    );
}
